import { useCallback, useEffect, useState } from 'react';
import Icon from '../app/components/Icon';
import { useApp } from '../app/lib/AppContext';
import { LOCALE } from '../app/lib/format';
import { STATUS_LABELS, categoryLabel } from '../app/lib/feedback';

// Admin panel › Góp ý người dùng: every thread from the floating "Góp ý" button (personal app and coach mode),
// the ones waiting for an answer first. A thread shows its messages, screenshots and photos, and what the app
// sent along; the reply goes out under the admin's name and lands in the user's bell. The server checks the
// admin session; this page only hides itself.

const FILTERS = [
  { id: 'waiting', label: 'Chờ trả lời' },
  { id: 'open', label: 'Đang mở' },
  { id: 'resolved', label: 'Đã xử lý' },
  { id: 'closed', label: 'Đã đóng' },
  { id: '', label: 'Tất cả' },
];
const CONTEXT_LABELS = { app_version: 'Phiên bản', os: 'Hệ điều hành', device: 'Máy', screen: 'Màn hình', date: 'Ngày đang xem', path: 'Đường dẫn' };
const sentAt = (iso) => new Date(iso).toLocaleString(LOCALE, { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });
const STATUS_STYLES = { open: 'bg-rest-bg text-rest', resolved: 'bg-[#e3f1e9] text-[#17804f]', closed: 'bg-base-200 text-muted' };

function StatusChip({ status }) {
  return <span className={`shrink-0 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[status]}`}>{STATUS_LABELS[status]}</span>;
}

function Who({ thread }) {
  const { user } = thread;
  return (
    <span>
      {user.name || 'Không tên'}
      {user.phone && <> · <a className="text-brand-text underline" href={`tel:${user.phone}`}>{user.phone}</a></>}
      {user.email && <> · {user.email}</>}
    </span>
  );
}

function Thread({ id, onBack, onChanged }) {
  const { api } = useApp();
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { api.get(`/admin/feedback_threads/${id}`).then(setThread).catch((e) => setError(e.message)); }, [api, id]);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await fn();
      setThread(updated);
      onChanged(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const send = (resolve) => run(async () => {
    const updated = await api.post(`/admin/feedback_threads/${id}/reply`, { body: reply, resolve });
    setReply('');
    return updated;
  });
  const setStatus = (status) => run(() => api.patch(`/admin/feedback_threads/${id}`, { status }));

  const ctx = thread?.context || {};
  const meal = ctx.meal;
  return (
    <div className="flex flex-col gap-4">
      <button type="button" className="flex min-h-11 w-fit items-center gap-1 font-semibold" onClick={onBack}>
        <Icon name="chevronLeft" className="size-5" /> Tất cả góp ý
      </button>
      {!thread && !error && <div className="skeleton h-64 rounded-[20px]" />}
      {thread && (
        <>
          <section className="flex flex-col gap-2 rounded-[20px] bg-base-100 p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h1 className="text-xl font-bold">#{thread.code} · {categoryLabel(thread.category)}</h1>
                <p className="text-sm"><Who thread={thread} /> · {thread.mode === 'coach' ? 'Chế độ HLV' : 'App cá nhân'}</p>
              </div>
              <StatusChip status={thread.status} />
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              {Object.entries(CONTEXT_LABELS).filter(([k]) => ctx[k]).map(([k, label]) => (
                <div key={k} className="contents"><dt className="text-muted">{label}</dt><dd className="break-all">{ctx[k]}</dd></div>
              ))}
              <dt className="text-muted">User id</dt><dd>{thread.user.id}</dd>
            </dl>
            {meal && (
              <div className="rounded-[14px] bg-base-200 p-3 text-sm">
                <p className="font-semibold">Món AI tính: {meal.description || meal.ai_summary} · {meal.date}</p>
                <p>{Math.round(meal.calories || 0)} kcal · đạm {meal.protein_g ?? '–'} g · tinh bột {meal.carbs_g ?? '–'} g · béo {meal.fat_g ?? '–'} g</p>
                {meal.ai_summary && <p className="text-muted">{meal.ai_summary}</p>}
                {meal.items?.length > 0 && <p className="text-muted">{meal.items.map((i) => `${i.name}${i.portion ? ` (${i.portion})` : ''}`).join(', ')}</p>}
              </div>
            )}
          </section>

          <section className="flex flex-col gap-3 rounded-[20px] bg-base-100 p-4">
            {thread.messages.map((m) => (
              <div key={m.id} className={`flex flex-col gap-1 ${m.sender === 'user' ? 'items-end' : 'items-start'}`}>
                <span className="text-[11px] text-muted">
                  {m.sender === 'user' ? 'Người dùng' : m.sender === 'bot' ? 'Bot' : `${m.staff_name} · Macro Mate`} · {sentAt(m.created_at)}
                  {m.sender === 'staff' && (m.read_at ? ' · đã xem' : ' · chưa xem')}
                </span>
                {m.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {m.attachments.map((a) => (
                      <a key={a.url} href={a.url} target="_blank" rel="noreferrer" title={a.kind}>
                        <img src={a.url} alt={a.kind} className={`rounded-[10px] border border-line object-cover ${a.kind === 'screenshot' ? 'h-48 w-24' : 'size-24'}`} />
                      </a>
                    ))}
                  </div>
                )}
                {m.body && (
                  <p className={`max-w-[85%] whitespace-pre-line rounded-[14px] px-3 py-2 text-[15px] ${m.sender === 'user' ? 'bg-[#17804f] text-white' : 'bg-base-200'}`}>{m.body}</p>
                )}
              </div>
            ))}
          </section>

          <section className="flex flex-col gap-3 rounded-[20px] bg-base-100 p-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold">Trả lời (người dùng thấy trong khung chat và nhận thông báo)</span>
              <textarea className="textarea min-h-24 w-full rounded-[14px] border-line text-base" value={reply}
                onChange={(e) => setReply(e.target.value)} placeholder="Viết câu trả lời…" />
            </label>
            {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
            <div className="flex flex-wrap gap-2">
              <button type="button" className="btn btn-primary h-11 rounded-[14px] border-0 px-4" disabled={busy || !reply.trim()} onClick={() => send(true)}>
                Trả lời & gắn "Đã xử lý"
              </button>
              <button type="button" className="btn h-11 rounded-[14px] border-line bg-base-100 px-4" disabled={busy || !reply.trim()} onClick={() => send(false)}>
                Chỉ trả lời
              </button>
              {thread.status !== 'resolved' && thread.status !== 'closed' && (
                <button type="button" className="btn h-11 rounded-[14px] border-line bg-base-100 px-4" disabled={busy} onClick={() => setStatus('resolved')}>Gắn "Đã xử lý"</button>
              )}
              {thread.status !== 'open' && (
                <button type="button" className="btn h-11 rounded-[14px] border-line bg-base-100 px-4" disabled={busy} onClick={() => setStatus('open')}>Mở lại</button>
              )}
              {thread.status !== 'closed' && (
                <button type="button" className="btn h-11 rounded-[14px] border-line bg-base-100 px-4" disabled={busy} onClick={() => setStatus('closed')}>Đóng</button>
              )}
            </div>
          </section>
        </>
      )}
      {error && !thread && <div role="alert" className="rounded-[20px] bg-[#fdebe4] p-4 text-sm text-[#8e3414]">{error}</div>}
    </div>
  );
}

export default function FeedbackPage() {
  const { api, admin } = useApp();
  const [filter, setFilter] = useState('waiting');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // ?thread=<id> (from a user's page) opens that conversation.
  const [openId, setOpenId] = useState(() => Number(new URLSearchParams(window.location.search).get('thread')) || null);

  const load = useCallback((more = false) => {
    const params = new URLSearchParams();
    if (filter) params.set('status', filter);
    if (more && data?.threads.length) params.set('before', data.threads[data.threads.length - 1].id);
    return api.get(`/admin/feedback_threads?${params}`)
      .then((res) => setData((prev) => (more ? { ...res, threads: [...prev.threads, ...res.threads] } : res)))
      .catch((e) => setError(e.message));
  }, [api, filter, data]);

  useEffect(() => { setData(null); load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!admin) return <p className="rounded-[20px] bg-base-100 p-5 text-sm">Trang này chỉ dành cho quản trị viên.</p>;
  if (openId) {
    return (
      <Thread id={openId} onBack={() => { setOpenId(null); setData(null); load(); }}
        onChanged={(t) => setData((prev) => prev && { ...prev, threads: prev.threads.map((r) => (r.id === t.id ? { ...r, ...t } : r)) })} />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-[22px] font-bold">Góp ý của người dùng</h1>

      <div role="tablist" className="flex gap-1 overflow-x-auto rounded-[16px] bg-base-300/70 p-1">
        {FILTERS.map((f) => (
          <button key={f.id} type="button" role="tab" aria-selected={filter === f.id} onClick={() => setFilter(f.id)}
            className={`h-11 shrink-0 grow rounded-[12px] px-3 text-sm font-semibold ${filter === f.id ? 'bg-base-100 shadow-sm' : 'text-muted'}`}>
            {f.label}{f.id && data?.counts?.[f.id] != null ? ` (${data.counts[f.id]})` : ''}
          </button>
        ))}
      </div>

      {error && <div role="alert" className="rounded-[20px] bg-[#fdebe4] p-4 text-sm text-[#8e3414]">{error}</div>}
      {!data && !error && <div className="skeleton h-48 rounded-[20px]" />}
      {data?.threads.length === 0 && (
        <p className="rounded-[20px] border border-dashed border-line p-5 text-center text-sm text-muted">Không có góp ý nào.</p>
      )}
      {data?.threads.length > 0 && (
        <ul className="divide-y divide-line rounded-[20px] bg-base-100 px-4">
          {data.threads.map((t) => (
            <li key={t.id}>
              <button type="button" className="flex w-full items-start gap-3 py-3 text-left" onClick={() => setOpenId(t.id)}>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs text-muted">
                    #{t.code} · {categoryLabel(t.category)} · {t.mode === 'coach' ? 'HLV' : 'Cá nhân'} · {sentAt(t.last_message_at)}
                  </span>
                  <span className="block text-sm font-semibold"><Who thread={t} /></span>
                  <span className="block truncate text-[15px]">{t.preview || 'Ảnh đính kèm'}</span>
                </span>
                <span className="flex flex-col items-end gap-1">
                  <StatusChip status={t.status} />
                  {t.last_sender !== 'staff' && t.status === 'open' && <span className="text-[11px] font-semibold text-error">Chờ trả lời</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {data?.more && (
        <button type="button" className="btn h-11 rounded-[14px] border-line bg-base-100" onClick={() => load(true)}>Xem thêm</button>
      )}
    </div>
  );
}
